import React, { useState, Children } from 'react';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { EBTWICBanner } from '../components/layout/EBTWICBanner';
import { TrustBadge } from '../components/ui/TrustBadge';
import { ChevronDown, Phone, Mail, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm" data-id="element-397">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 md:p-6 text-left hover:bg-gray-50 transition-colors" data-id="element-398">
        <span className="font-bold text-gray-900 text-base md:text-lg pr-4" data-id="element-399">
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 md:w-6 md:h-6 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-400" />
      </button>
      <AnimatePresence data-id="element-401">
        {isOpen && <motion.div initial={{
        height: 0
      }} animate={{
        height: 'auto'
      }} exit={{
        height: 0
      }} className="overflow-hidden" data-id="element-402">
            <div className="px-4 md:px-6 pb-4 md:pb-6 text-gray-600 leading-relaxed border-t border-gray-50 text-sm md:text-base" data-id="element-403">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function WICEBTGuidePage() {
  return <div className="min-h-screen bg-[#FAF8F5]" data-id="element-404">
      <EBTWICBanner data-id="element-405" />
      <TopNav data-id="element-406" />

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16 pb-24 md:pb-16" data-id="element-407">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16" data-id="element-408">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6" data-id="element-409">
            WIC & EBT Guide
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-410">
            We're committed to making healthy, culturally appropriate food
            accessible to everyone in the Lanham community.
          </p>
        </div>

        {/* Program Cards */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-12 md:mb-16" data-id="element-411">
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-412">
            <TrustBadge type="WIC" className="mb-4" size="lg" data-id="element-413" />
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3" data-id="element-414">
              WIC Program
            </h3>
            <p className="text-gray-600 leading-relaxed text-sm md:text-base" data-id="element-415">
              Women, Infants, and Children nutrition program approved items. We
              carry a wide selection of WIC-eligible products including baby
              formula, fresh produce, and whole grains.
            </p>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-416">
            <TrustBadge type="EBT" className="mb-4" size="lg" data-id="element-417" />
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3" data-id="element-418">
              SNAP / EBT
            </h3>
            <p className="text-gray-600 leading-relaxed text-sm md:text-base" data-id="element-419">
              Supplemental Nutrition Assistance Program payments accepted. Shop
              with confidence knowing most of our products are EBT-eligible.
            </p>
          </div>
        </div>

        {/* How to Shop Section */}
        <section className="bg-white p-6 md:p-10 rounded-2xl shadow-sm border border-gray-100 mb-12 md:mb-16" data-id="element-420">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8" data-id="element-421">
            How to Shop with Benefits
          </h2>

          <div className="space-y-6 md:space-y-8" data-id="element-422">
            <div className="flex gap-4 md:gap-6" data-id="element-423">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-[#D97757] to-[#F4A261] text-white flex items-center justify-center font-bold text-lg md:text-xl flex-shrink-0 shadow-md" data-id="element-424">
                1
              </div>
              <div data-id="element-425">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2" data-id="element-426">
                  Look for the Badges
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base" data-id="element-427">
                  Items eligible for WIC or EBT are clearly marked with badges
                  on the product card. This makes it easy to identify what you
                  can purchase with your benefits.
                </p>
              </div>
            </div>

            <div className="flex gap-4 md:gap-6" data-id="element-428">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-[#D97757] to-[#F4A261] text-white flex items-center justify-center font-bold text-lg md:text-xl flex-shrink-0 shadow-md" data-id="element-429">
                2
              </div>
              <div data-id="element-430">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2" data-id="element-431">
                  Filter Your Search
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base" data-id="element-432">
                  Use the "EBT Eligible Only" toggle in the search page to see
                  only items you can buy with your benefits. This helps you shop
                  faster and with confidence.
                </p>
              </div>
            </div>

            <div className="flex gap-4 md:gap-6" data-id="element-433">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-[#D97757] to-[#F4A261] text-white flex items-center justify-center font-bold text-lg md:text-xl flex-shrink-0 shadow-md" data-id="element-434">
                3
              </div>
              <div data-id="element-435">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2" data-id="element-436">
                  Checkout Securely
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base" data-id="element-437">
                  At checkout, select "EBT/SNAP" as your payment method. You'll
                  enter your PIN securely, and we'll process your benefits
                  payment immediately.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-12 md:mb-16" data-id="element-438">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8" data-id="element-439">
            Frequently Asked Questions
          </h2>

          <div className="space-y-3 md:space-y-4" data-id="element-440">
            <FAQItem question="Can I use WIC for online orders?" answer="Yes! J&J FOODS is one of the few approved online retailers for WIC in Maryland. You can order online and pay with your WIC card at delivery or pickup." data-id="element-441" />
            <FAQItem question="What if an item is out of stock?" answer="Our system will suggest EBT-eligible substitutions. We will never substitute a WIC item without your explicit approval." data-id="element-442" />
            <FAQItem question="Is there a delivery fee for EBT orders?" answer="Orders over $35 qualify for free delivery, regardless of payment method. For smaller orders, the standard $5.99 fee applies." data-id="element-443" />
            <FAQItem question="How do I know if a product is WIC or EBT eligible?" answer="Every product page displays clear WIC and EBT badges if the item is eligible. You can also use our filter to show only eligible items." data-id="element-444" />
          </div>
        </section>

        {/* Contact Section */}
        <section className="bg-gradient-to-br from-[#1E3A5F] to-[#2D5F3F] text-white p-6 md:p-10 rounded-2xl shadow-lg" data-id="element-445">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6" data-id="element-446">
            Need Help?
          </h2>
          <p className="text-blue-100 mb-6 md:mb-8 text-base md:text-lg" data-id="element-447">
            Our team is here to assist you with any questions about using your
            benefits.
          </p>

          <div className="space-y-4 md:space-y-5" data-id="element-448">
            <a href="tel:3014299440" className="flex items-center gap-3 md:gap-4 text-base md:text-lg hover:text-[#F4A261] transition-colors group" data-id="element-449">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-colors" data-id="element-450">
                <Phone className="w-5 h-5 md:w-6 md:h-6" data-id="element-451" />
              </div>
              <span className="font-semibold" data-id="element-452">(301) 429-9440</span>
            </a>
            <a href="mailto:help@jjfoods.com" className="flex items-center gap-3 md:gap-4 text-base md:text-lg hover:text-[#F4A261] transition-colors group" data-id="element-453">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-colors" data-id="element-454">
                <Mail className="w-5 h-5 md:w-6 md:h-6" data-id="element-455" />
              </div>
              <span className="font-semibold" data-id="element-456">help@jjfoods.com</span>
            </a>
            <a href="https://www.fns.usda.gov/wic" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 md:gap-4 text-base md:text-lg hover:text-[#F4A261] transition-colors group" data-id="element-457">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-colors" data-id="element-458">
                <ExternalLink className="w-5 h-5 md:w-6 md:h-6" data-id="element-459" />
              </div>
              <span className="font-semibold" data-id="element-460">Visit Maryland WIC Website</span>
            </a>
          </div>
        </section>
      </main>

      <BottomNav data-id="element-461" />
    </div>;
}